import { useMemo } from 'react'
import wifi from '@/state/wifi/wifi.slice'
import { type VendorType } from '@/state/wifi/types'

/**
 * The Wi-Fi hardware tier a venue falls into. This is the single
 * discriminator the nav and the hardware surfaces branch on — see
 * `docs/wifi-hardware-simplification/`.
 *
 * - `snapguard`     — Stampede's own MikroTik offering: a SnapProvisioning
 *                     link exists (vendor is `mikrotik` AND a device is
 *                     linked). Deepest integration.
 * - `unifi`         — bring-your-own UniFi controller.
 * - `mikrotik`      — a MikroTik router the customer owns, not Snap-linked.
 * - `meraki`        — Cisco Meraki via cloud RADIUS: full accounting
 *                     (Start/Stop, usage, session end), so sessions are
 *                     complete — the full journey, not just auth events.
 * - `click-through` — any other configured vendor (auth handshake only,
 *                     no usage/accounting data).
 * - `unconfigured`  — no vendor set yet (or `none`); safe fallback.
 */
export type HardwareTier =
  | 'snapguard'
  | 'unifi'
  | 'mikrotik'
  | 'meraki'
  | 'click-through'
  | 'unconfigured'

/**
 * Pure tier resolver. Kept separate from the hook so it's trivial to reason
 * about and test: tier is a function of the vendor key plus whether a Snap
 * device is linked. `vpn_is_online` and other live health signals never
 * affect the tier — they drive badges, not nav shape.
 */
export function resolveHardwareTier(
  vendorKey: VendorType | undefined | null,
  hasSnapProvisioning: boolean
): HardwareTier {
  if (!vendorKey || vendorKey === 'none') return 'unconfigured'
  if (vendorKey === 'mikrotik') {
    return hasSnapProvisioning ? 'snapguard' : 'mikrotik'
  }
  if (vendorKey === 'unifi') return 'unifi'
  if (vendorKey === 'meraki') return 'meraki'
  return 'click-through'
}

export interface HardwareTierResult {
  tier: HardwareTier
  vendorKey: VendorType | undefined
  /** True while the signals needed to decide the tier are still loading. */
  isLoading: boolean
  /** True once a Snap device link has been confirmed for this venue. */
  isSnapGuard: boolean
}

/**
 * Resolve a venue's hardware tier from its serial. Works standalone (outside
 * the wi-fi outlet context) so the sidebar nav can call it. Uses the same
 * RTK Query endpoints the pages use, so the cache is shared — no extra
 * network cost. The MikroTik status query (which carries the Snap
 * `provisioning` link) is skipped unless the vendor is MikroTik.
 */
export function useHardwareTier(
  serial: string | undefined
): HardwareTierResult {
  const { data: inform, isLoading: informLoading } = wifi.useGetInformQuery(
    { serial: serial ?? '' },
    { skip: !serial }
  )

  const vendorKey = inform?.vendor?.key

  const { data: health, isLoading: healthLoading } =
    wifi.useGetMikrotikStatusQuery(
      { serial: serial ?? '' },
      { skip: !serial || vendorKey !== 'mikrotik' }
    )

  const isSnapGuard = Boolean(health?.provisioning)

  return useMemo(() => {
    const tier = resolveHardwareTier(vendorKey, isSnapGuard)
    // Only wait on the MikroTik status when it actually decides the tier.
    const isLoading =
      informLoading || (vendorKey === 'mikrotik' && healthLoading)
    return { tier, vendorKey, isLoading, isSnapGuard }
  }, [vendorKey, isSnapGuard, informLoading, healthLoading])
}
